import { create } from 'zustand';
import { chatApi, type SupportChatMessage } from '../api/client';

interface PendingAction {
  tool: string;
  params: Record<string, unknown>;
}

interface ChatState {
  messages: SupportChatMessage[];
  isOpen: boolean;
  isLoading: boolean;
  pendingAction: PendingAction | null;
  conversationId: string | null;
  connectionFailed: boolean;

  // Actions
  sendMessage: (content: string) => Promise<void>;
  createConversation: (surface: 'app' | 'marketing') => Promise<void>;
  retryConnection: (surface: 'app' | 'marketing') => Promise<void>;
  submitFeedback: (messageId: string, rating: 'helpful' | 'unhelpful') => Promise<void>;
  toggleChat: () => void;
  setOpen: (open: boolean) => void;
}

export const useChatStore = create<ChatState>()((set, get) => ({
  messages: [],
  isOpen: false,
  isLoading: false,
  pendingAction: null,
  conversationId: null,
  connectionFailed: false,

  sendMessage: async (content: string) => {
    const { conversationId, isLoading } = get();
    if (isLoading) return;
    if (!conversationId) {
      set((state) => ({
        messages: [
          ...state.messages,
          {
            id: `user-${Date.now()}`,
            role: 'user' as const,
            content,
            created_at: new Date().toISOString(),
          },
          {
            id: `error-${Date.now()}`,
            role: 'assistant' as const,
            content: "I'm having trouble connecting right now. Please try closing and reopening the chat, or try again in a moment.",
            created_at: new Date().toISOString(),
          },
        ],
      }));
      return;
    }

    const userMessage: SupportChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
      isLoading: true,
    }));

    try {
      const response = await chatApi.sendMessage(conversationId, content);
      set((state) => ({
        messages: [...state.messages, response],
        isLoading: false,
        pendingAction: response.pendingAction
          ? (state.pendingAction ?? null)
          : null,
      }));
    } catch {
      set((state) => ({
        messages: [
          ...state.messages,
          {
            id: `error-${Date.now()}`,
            role: 'assistant' as const,
            content: 'Sorry, something went wrong. Please try again.',
            created_at: new Date().toISOString(),
          },
        ],
        isLoading: false,
      }));
    }
  },

  createConversation: async (surface: 'app' | 'marketing') => {
    try {
      const conversation = await chatApi.createConversation(surface);
      set({ conversationId: conversation.id, connectionFailed: false });
    } catch {
      console.error('Failed to create conversation');
      set((state) => ({
        connectionFailed: true,
        messages: [
          ...state.messages,
          {
            id: `error-${Date.now()}`,
            role: 'assistant' as const,
            content: "I'm having trouble connecting. Please try again in a moment.",
            created_at: new Date().toISOString(),
          },
        ],
      }));
    }
  },

  retryConnection: async (surface: 'app' | 'marketing') => {
    set({ connectionFailed: false, messages: [] });
    try {
      const conversation = await chatApi.createConversation(surface);
      set({ conversationId: conversation.id, connectionFailed: false });
    } catch {
      console.error('Failed to create conversation on retry');
      set((state) => ({
        connectionFailed: true,
        messages: [
          ...state.messages,
          {
            id: `error-${Date.now()}`,
            role: 'assistant' as const,
            content: "Still unable to connect. The service may be temporarily unavailable. Please try again later.",
            created_at: new Date().toISOString(),
          },
        ],
      }));
    }
  },

  submitFeedback: async (messageId: string, rating: 'helpful' | 'unhelpful') => {
    try {
      await chatApi.submitFeedback(messageId, rating);
    } catch {
      console.error('Failed to submit feedback');
    }
  },

  toggleChat: () => {
    set((state) => ({ isOpen: !state.isOpen }));
  },

  setOpen: (open: boolean) => {
    set({ isOpen: open });
  },
}));
